/**
 * Main layout component with navigation
 */
import { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { SessionCountdown, useAuth } from '@codespeak11/cognito-passwordless';
import { useSignOutConfirm } from '../auth/signOut';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ArticleIcon from '@mui/icons-material/Article';
import InventoryIcon from '@mui/icons-material/Inventory';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PublicIcon from '@mui/icons-material/Public';import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

const drawerWidth = 240;

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
  { path: '/projects', label: 'Projects', icon: <FolderOpenIcon /> },
  { path: '/prompts', label: 'Prompts', icon: <LibraryBooksIcon /> },
  { path: '/runner', label: 'Prompt Runner', icon: <PlayCircleOutlineIcon /> },
  { path: '/templates', label: 'Templates', icon: <ArticleIcon /> },
  { path: '/packs', label: 'Packs', icon: <InventoryIcon /> },
  { path: '/library', label: 'Library', icon: <PublicIcon /> },
];

export default function Layout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [headerSearch, setHeaderSearch] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { requestSignOut, dialog: signOutDialog } = useSignOutConfirm();

  const handleHeaderSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && headerSearch.trim()) {
      navigate(`/dashboard?q=${encodeURIComponent(headerSearch.trim())}`);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleMenuClose();
    requestSignOut();
  };

  const handleMenuNavigate = (path: string) => {
    handleMenuClose();
    navigate(path);
  };

  const drawer = (
    <Box>
      <Toolbar
        disableGutters
        sx={{
          overflow: 'visible',
          px: 1,
          // Allow the logo to spill out to the right beyond the drawer width
          position: 'relative',
        }}
      >
        <Box
          component="img"
          src="/img/TopLogo.png"
          alt="PromptFab"
          onClick={() => navigate('/dashboard')}
          sx={{
            height: 73,
            width: 'auto',
            maxWidth: 'none',
            borderRadius: 1,
            cursor: 'pointer',
            position: 'relative',
            zIndex: (t) => t.zIndex.drawer + 2,
          }}
        />
      </Toolbar>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />
          {/* Nothing renders until the session is within 15 minutes of expiring. */}
          <SessionCountdown />

          {/* Header Search */}
          <TextField
            size="small"
            placeholder="Search projects, prompts, templates, packs..."
            value={headerSearch}
            onChange={(e) => setHeaderSearch(e.target.value)}
            onKeyDown={handleHeaderSearchSubmit}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: 180, sm: 280, md: 360 },
              maxWidth: 400,
              mr: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.15)',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.6)' },
                '&.Mui-focused fieldset': { borderColor: 'white' },
              },
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.7)' },
              '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' },
            }}
          />

          {/* User Menu */}
          <Button
            onClick={handleMenuOpen}
            disableRipple
            sx={{
              minWidth: 0,
              p: 0.5,
              borderRadius: 6,
              color: 'inherit',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'success.main',
                fontSize: '0.9rem',
                fontWeight: 700,
              }}
            >
              {user?.email?.[0]?.toUpperCase() || <PersonIcon />}
            </Avatar>
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 18,
                color: 'rgba(255,255,255,0.85)',
                transition: 'transform 0.2s',
                transform: anchorEl !== null ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </Button>
          
          <Menu
            anchorEl={anchorEl}
            open={anchorEl !== null}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'success.main', fontSize: '1rem', fontWeight: 700 }}>
                {user?.email?.[0]?.toUpperCase() || <PersonIcon />}
              </Avatar>
              <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.email || 'User'}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => handleMenuNavigate('/settings')}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <MenuItem onClick={() => handleMenuNavigate('/billing')}>
              <ListItemIcon>
                <CreditCardIcon fontSize="small" />
              </ListItemIcon>
              Billing
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              overflow: 'visible',
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              overflow: 'visible',
              borderRight: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1 }}>
          <Outlet />
        </Box>
        <Box
          component="footer"
          sx={{
            mt: 6,
            px: { xs: 2, md: 4 },
            py: 3,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: { xs: 1.5, sm: 3 },
              maxWidth: '100%',
            }}
          >
            <Box
              component="a"
              href="https://BrainDance.com/sitedirectory"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
            >
              <Box
                component="img"
                src="/img/bdancebanner.png"
                alt="BrainDance Family of Websites"
                sx={{ height: 90, width: 'auto', display: 'block', borderRadius: '12px' }}
              />
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: { xs: 'center', sm: 'left' },
                fontSize: { xs: '0.875rem', sm: '1.275rem' },
                lineHeight: 1.4,
              }}
            >
              Prompt Fabricator is part of the{' '}
              <Box
                component="a"
                href="https://www.braindance.com/sitedirectory"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 600,
                  whiteSpace: { sm: 'nowrap' },
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                BrainDance Family of Websites
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>
      {signOutDialog}
    </Box>
  );
}
